/**
 * StageDescriptionField — editable per-stage description.
 *
 * Rendered full-width beneath a stage's controls via StageListEditor's
 * `renderRowFooter`. Shared by the create and edit pipeline slideouts so both
 * surfaces behave identically. Collapsed to a single clipped line when blurred;
 * grows to fit the full text (via `field-sizing-content`) once focused.
 *
 * The description guides the AI teammate on when a record enters or exits the
 * stage.
 */

import { useCallback, useState } from 'react';

import { TextArea } from '@/lib/base/forms/textarea/textarea';
import { cx } from '@/utils/cx';

const PLACEHOLDER =
  'Describe when a record enters or exits this stage. Your AI teammate uses this to move records through the pipeline.';

export interface StageDescriptionFieldProps {
  stageId: string;
  stageName: string;
  value: string;
  disabled: boolean;
  onChange: (id: string, description: string) => void;
}

export function StageDescriptionField({
  stageId,
  stageName,
  value,
  disabled,
  onChange,
}: StageDescriptionFieldProps) {
  const [isFocused, setIsFocused] = useState(false);
  const handleChange = useCallback(
    (next: string) => onChange(stageId, next),
    [onChange, stageId]
  );
  return (
    <TextArea
      aria-label={`Description for ${stageName || 'stage'}`}
      value={value}
      onChange={handleChange}
      onFocusChange={setIsFocused}
      isDisabled={disabled}
      rows={1}
      resize='none'
      placeholder={PLACEHOLDER}
      // Until focused, the field is low-contrast — muted hint text and a faint
      // (but still visible) border so it's clearly clickable; the border
      // strengthens on hover and focusing restores the full box + primary text.
      wrapperClassName={cx(
        !isFocused &&
          'bg-transparent shadow-none ring-secondary hover:ring-primary'
      )}
      textareaClassName={cx(
        'text-sm',
        isFocused
          ? 'field-sizing-content whitespace-normal'
          : 'overflow-hidden whitespace-nowrap text-ellipsis text-tertiary'
      )}
    />
  );
}
